import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc, collection, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { agoraConfig } from '../../firebase/agoraConfig';
import { createVideoCall, endVideoCall as endVideoCallDB } from '../../utils/videoCallDatabase';

// Dynamic import for Agora (only load if available)
let AgoraRTC = null;
try {
  AgoraRTC = require('agora-rtc-sdk-ng');
} catch (error) {
  console.warn('Agora RTC SDK not installed. Run: npm install agora-rtc-sdk-ng');
}

const VideoCall = () => {
  const { chatId, productId, storeId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [otherUser, setOtherUser] = useState(null);
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callStatus, setCallStatus] = useState('connecting'); // connecting, ringing, active, ended
  const [error, setError] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [connectionState, setConnectionState] = useState('disconnected'); // disconnected, connecting, connected
  const [videoLayout, setVideoLayout] = useState('split'); // 'split', 'remote-full', 'local-full'
  const [ringingAudio, setRingingAudio] = useState(null);
  const [incomingCallAudio, setIncomingCallAudio] = useState(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const clientRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const remoteUsersRef = useRef([]);
  const callIdRef = useRef(null);
  const receiverIdRef = useRef(null);
  const isJoiningRef = useRef(false);
  const isMountedRef = useRef(true);
  const ringingAudioRef = useRef(null); // Use ref to avoid stale closure

  useEffect(() => {
    isMountedRef.current = true;
    loadCallInfo();
    return () => {
      isMountedRef.current = false;
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      // Stop ringing sound using ref (avoid stale closure)
      if (ringingAudioRef.current) {
        if (ringingAudioRef.current.interval) {
          clearInterval(ringingAudioRef.current.interval);
        }
        if (ringingAudioRef.current.audioContext) {
          try {
            ringingAudioRef.current.audioContext.close();
          } catch (e) {
            console.warn('Error closing ringing audio context:', e);
          }
        }
        ringingAudioRef.current = null;
        setRingingAudio(null);
      }
      // Cleanup Agora resources
      if (isJoiningRef.current) {
        console.warn('⚠️ Component unmounting during join, cleaning up...');
      }
      leaveAgoraChannel().catch(err => {
        console.error('Error during cleanup:', err);
      });
    };
  }, [chatId, productId, storeId]);

  const loadCallInfo = async () => {
    try {
      setLoading(true);
      
      // Load chat info if chatId exists
      if (chatId) {
        const chatRef = doc(db, 'chats', chatId);
        const chatSnap = await getDoc(chatRef);
        
        if (chatSnap.exists()) {
          const chatData = chatSnap.data();
          const participants = Array.isArray(chatData.participants) ? chatData.participants : [];
          const otherUserId = participants.find(id => id && id !== currentUser?.uid);
          
          console.log('📞 Loading chat info:', {
            chatId,
            participants,
            currentUserId: currentUser?.uid,
            otherUserId
          });
          
          if (otherUserId) {
            const userRef = doc(db, 'users', otherUserId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              setOtherUser({ id: userSnap.id, ...userSnap.data() });
              console.log('✅ Loaded other user:', otherUserId);
            } else {
              console.warn('⚠️ User not found:', otherUserId);
            }
          } else {
            console.warn('⚠️ Could not find other user in participants');
          }
          
          // Load product if productId exists
          if (chatData.productId) {
            const productRef = doc(db, 'products', chatData.productId);
            const productSnap = await getDoc(productRef);
            if (productSnap.exists()) {
              setProduct({ id: productSnap.id, ...productSnap.data() });
            }
          }
          
          // Load store if storeId exists
          if (chatData.storeId) {
            const storeRef = doc(db, 'stores', chatData.storeId);
            const storeSnap = await getDoc(storeRef);
            if (storeSnap.exists()) {
              const storeData = { id: storeSnap.id, ...storeSnap.data() };
              setStore(storeData);
              console.log('✅ Loaded store:', storeData.id, 'ownerId:', storeData.ownerId);
            }
          }
        } else {
          console.error('❌ Chat not found:', chatId);
        }
      } else if (productId && storeId) {
        // Load product and store directly
        const productRef = doc(db, 'products', productId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          setProduct({ id: productSnap.id, ...productSnap.data() });
        }
        
        const storeRef = doc(db, 'stores', storeId);
        const storeSnap = await getDoc(storeRef);
        if (storeSnap.exists()) {
          const storeData = { id: storeSnap.id, ...storeSnap.data() };
          setStore(storeData);
          console.log('✅ Loaded store:', storeData.id, 'ownerId:', storeData.ownerId);
          
          // Load store owner as other user
          if (storeData.ownerId) {
            const userRef = doc(db, 'users', storeData.ownerId);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const userData = { id: userSnap.id, ...userSnap.data() };
              setOtherUser(userData);
              console.log('✅ Loaded store owner as other user:', userData.id);
            } else {
              console.warn('⚠️ Store owner not found:', storeData.ownerId);
            }
          } else {
            console.error('❌ Store has no ownerId:', storeData);
          }
        } else {
          console.error('❌ Store not found:', storeId);
          throw new Error('ไม่พบข้อมูลร้านค้า');
        }
      }
      
      // Verify receiverId is available before starting call
      let verifiedReceiverId = null;
      
      // Get receiverId from loaded data
      if (otherUser?.id) {
        verifiedReceiverId = otherUser.id;
      } else if (store?.ownerId) {
        verifiedReceiverId = store.ownerId;
      }
      
      // If still not found, try to get from database directly
      if (!verifiedReceiverId) {
        if (chatId) {
          const chatRef = doc(db, 'chats', chatId);
          const chatSnap = await getDoc(chatRef);
          if (chatSnap.exists()) {
            const chatData = chatSnap.data();
            const participants = Array.isArray(chatData.participants) ? chatData.participants : [];
            const otherUserId = participants.find(id => id && id !== currentUser?.uid);
            if (otherUserId) {
              verifiedReceiverId = otherUserId;
              console.log('✅ Got receiverId from chat:', verifiedReceiverId);
            }
          }
        } else if (storeId) {
          const storeRef = doc(db, 'stores', storeId);
          const storeSnap = await getDoc(storeRef);
          if (storeSnap.exists()) {
            const storeData = storeSnap.data();
            if (storeData.ownerId) {
              verifiedReceiverId = storeData.ownerId;
              console.log('✅ Got receiverId directly from store:', verifiedReceiverId);
            }
          }
        }
      }
      
      if (!verifiedReceiverId) {
        console.error('❌ Cannot determine receiverId. Available data:', {
          chatId,
          productId,
          storeId,
          otherUser: otherUser?.id,
          storeOwnerId: store?.ownerId
        });
        throw new Error('ไม่สามารถระบุผู้รับสายได้ กรุณาลองใหม่อีกครั้ง');
      }
      
      console.log('✅ ReceiverId verified before starting call:', verifiedReceiverId);
      
      // Store receiverId in ref to ensure it's available when startCall() is called
      receiverIdRef.current = verifiedReceiverId;
      
      // Play ringing sound function
      const playRingingSound = () => {
        try {
          // Stop any existing ringing sound first
          if (ringingAudioRef.current) {
            if (ringingAudioRef.current.interval) {
              clearInterval(ringingAudioRef.current.interval);
            }
            if (ringingAudioRef.current.audioContext) {
              try {
                ringingAudioRef.current.audioContext.close();
              } catch (e) {
                console.warn('Error closing existing audio context:', e);
              }
            }
          }
          
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const playTone = () => {
            // Check if still ringing before playing
            if (!isMountedRef.current || callStatus !== 'ringing') {
              return;
            }
            
            try {
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();
              
              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);
              
              oscillator.frequency.value = 800;
              oscillator.type = 'sine';
              
              gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
              gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
              
              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.5);
            } catch (e) {
              console.warn('Error playing ring tone:', e);
            }
          };
          
          playTone();
          const interval = setInterval(playTone, 2000);
          
          const audioData = { audioContext, interval };
          ringingAudioRef.current = audioData;
          setRingingAudio(audioData);
        } catch (error) {
          console.warn('Could not play ringing sound:', error);
        }
      };
      
      // Simulate call connection
      setTimeout(() => {
        setCallStatus('ringing');
        playRingingSound();
        // After 3 seconds, start the call
        setTimeout(() => {
          // Stop ringing sound using ref
          if (ringingAudioRef.current) {
            if (ringingAudioRef.current.interval) {
              clearInterval(ringingAudioRef.current.interval);
            }
            if (ringingAudioRef.current.audioContext) {
              try {
                ringingAudioRef.current.audioContext.close();
              } catch (e) {
                console.warn('Error closing ringing audio context:', e);
              }
            }
            ringingAudioRef.current = null;
            setRingingAudio(null);
          }
          startCall();
        }, 3000);
      }, 1000);
      
    } catch (error) {
      console.error('Error loading call info:', error);
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const startCall = async () => {
    try {
      // Stop ringing sound if playing (use ref to avoid stale closure)
      if (ringingAudioRef.current) {
        if (ringingAudioRef.current.interval) {
          clearInterval(ringingAudioRef.current.interval);
        }
        if (ringingAudioRef.current.audioContext) {
          try {
            ringingAudioRef.current.audioContext.close();
          } catch (e) {
            console.warn('Error closing ringing audio context:', e);
          }
        }
        ringingAudioRef.current = null;
        setRingingAudio(null);
      }
      
      setCallStatus('active');
      callStartTimeRef.current = new Date();
      
      // Start duration timer
      durationIntervalRef.current = setInterval(() => {
        if (callStartTimeRef.current) {
          const duration = Math.floor((new Date() - callStartTimeRef.current) / 1000);
          setCallDuration(duration);
        }
      }, 1000);
      
      // Validate required fields before creating call record
      if (!currentUser?.uid) {
        throw new Error('ไม่พบข้อมูลผู้เรียก (callerId)');
      }
      
      // Determine receiverId - must not be undefined or null
      // First, try to get from ref (set during loadCallInfo)
      let receiverId = receiverIdRef.current;
      
      // If not in ref, try to get from state
      if (!receiverId) {
        if (otherUser?.id) {
          receiverId = otherUser.id;
        } else if (store?.ownerId) {
          receiverId = store.ownerId;
        }
      }
      
      // If still not found, try to load from database
      if (!receiverId) {
        console.warn('⚠️ receiverId is missing. Attempting to load missing data...');
        
        if (chatId) {
          const chatRef = doc(db, 'chats', chatId);
          const chatSnap = await getDoc(chatRef);
          if (chatSnap.exists()) {
            const chatData = chatSnap.data();
            const participants = Array.isArray(chatData.participants) ? chatData.participants : [];
            const otherUserId = participants.find(id => id && id !== currentUser?.uid);
            if (otherUserId) {
              receiverId = otherUserId;
              console.log('✅ Found receiverId from chat:', receiverId);
            }
          }
        }
        
        // If still not found, try storeId
        if (!receiverId && storeId) {
          const storeRef = doc(db, 'stores', storeId);
          const storeSnap = await getDoc(storeRef);
          if (storeSnap.exists()) {
            const storeData = storeSnap.data();
            if (storeData.ownerId) {
              receiverId = storeData.ownerId;
              console.log('✅ Found receiverId from store:', receiverId);
            }
          }
        }
      }
      
      // Final validation - receiverId must be a valid string
      if (!receiverId || typeof receiverId !== 'string' || receiverId.trim() === '') {
        console.error('❌ receiverId validation failed:', {
          receiverId,
          type: typeof receiverId,
          chatId,
          storeId,
          otherUser: otherUser?.id,
          storeOwnerId: store?.ownerId
        });
        throw new Error('ไม่พบข้อมูลผู้รับสาย (receiverId). กรุณาลองใหม่อีกครั้ง');
      }
      
      console.log('✅ receiverId validated:', receiverId);
      
      // Create call record in Firestore
      const callData = {
        chatId: chatId || null,
        productId: productId || null,
        storeId: storeId || null,
        callerId: currentUser.uid,
        receiverId: receiverId, // Guaranteed to be a valid string
        status: 'active',
        startedAt: new Date()
      };
      
      // Remove any undefined or null values to prevent Firestore errors
      Object.keys(callData).forEach(key => {
        if (callData[key] === undefined || callData[key] === null) {
          // Keep null for optional fields, but remove undefined
          if (callData[key] === undefined) {
            delete callData[key];
          }
        }
      });
      
      // Final check: receiverId must exist and be a string
      if (!callData.receiverId || typeof callData.receiverId !== 'string') {
        throw new Error('receiverId is invalid. Expected string, got: ' + typeof callData.receiverId);
      }
      
      const call = await createVideoCall(callData);
      callIdRef.current = call.id;
      
      // Decrease reservedCalls when call starts (ไม่เพิ่ม totalCalls เพราะเพิ่มแล้วตอน approve)
      try {
        const { getVideoCallUsage } = await import('../../utils/videoCallDatabase');
        const usage = await getVideoCallUsage(currentUser?.uid, true);
        const usageRef = doc(db, 'videoCallUsage', usage.id);
        await updateDoc(usageRef, {
          reservedCalls: Math.max(0, (usage.reservedCalls || 0) - 1),
          updatedAt: serverTimestamp()
        });
        console.log('✅ Decreased reservedCalls for user:', currentUser?.uid);
      } catch (usageError) {
        console.error('Error updating reserved calls:', usageError);
        // Continue anyway - don't block call
      }
      
      // Initialize Agora RTC
      await initializeVideoCall();
      
    } catch (error) {
      console.error('Error starting call:', error);
      setError('เกิดข้อผิดพลาดในการเริ่ม Video Call: ' + (error.message || error));
      setCallStatus('ended');
    }
  };

  const initializeAgoraClient = async () => {
    if (!AgoraRTC) {
      console.warn('Agora RTC SDK not available');
      return null;
    }

    try {
      // Create Agora client
      const client = AgoraRTC.createClient({ 
        mode: "rtc", 
        codec: "vp8" 
      });
      
      // Handle user published (remote user joined)
      client.on("user-published", async (user, mediaType) => {
        try {
          await client.subscribe(user, mediaType);
          
          if (mediaType === "video") {
            remoteUsersRef.current = [...remoteUsersRef.current, user];
            // Attach remote video
            if (remoteVideoRef.current) {
              user.videoTrack.play(remoteVideoRef.current);
            }
            console.log('✅ Remote user video published:', user.uid);
          }
          
          if (mediaType === "audio") {
            user.audioTrack.play();
            console.log('✅ Remote user audio published:', user.uid);
          }
        } catch (error) {
          console.error('❌ Error subscribing to user:', error);
        }
      });

      // Handle user unpublished (remote user left)
      client.on("user-unpublished", (user, mediaType) => {
        if (mediaType === "video") {
          remoteUsersRef.current = remoteUsersRef.current.filter(u => u.uid !== user.uid);
          if (remoteVideoRef.current) {
            remoteVideoRef.current.innerHTML = '';
          }
          console.log('Remote user video unpublished:', user.uid);
        }
      });

      // Handle connection state changes
      client.on("connection-state-change", (curState, revState) => {
        console.log('Connection state changed:', revState, '->', curState);
        if (curState === 'CONNECTED') {
          setConnectionState('connected');
        } else if (curState === 'CONNECTING' || curState === 'RECONNECTING') {
          setConnectionState('connecting');
        } else if (curState === 'DISCONNECTED' || curState === 'FAILED') {
          setConnectionState('disconnected');
          if (callStatus === 'active') {
            setError('การเชื่อมต่อขาด กรุณาลองใหม่');
          }
        }
      });

      // Handle network quality
      client.on("network-quality", (stats) => {
        // Can be used to show network quality indicator
        console.log('Network quality:', stats);
      });

      return client;
    } catch (error) {
      console.error('Error initializing Agora client:', error);
      throw error;
    }
  };

  const joinAgoraChannel = async () => {
    // Check if component is still mounted
    if (!isMountedRef.current) {
      console.warn('⚠️ Component unmounted, skipping join');
      return;
    }
    
    // Prevent multiple simultaneous joins
    if (isJoiningRef.current) {
      console.warn('⚠️ Already joining channel, skipping duplicate call');
      return;
    }
    
    isJoiningRef.current = true;
    
    // Debug: Check Agora configuration
    console.log('🔧 Joining Agora Channel - Debug:', {
      hasAgoraRTC: !!AgoraRTC,
      hasAppId: !!agoraConfig.appId,
      appId: agoraConfig.appId ? `${agoraConfig.appId.substring(0, 8)}...` : 'NOT SET',
      envVar: process.env.REACT_APP_AGORA_APP_ID ? `${process.env.REACT_APP_AGORA_APP_ID.substring(0, 8)}...` : 'NOT SET'
    });
    
    if (!AgoraRTC) {
      console.error('❌ AgoraRTC SDK not loaded. Please run: npm install agora-rtc-sdk-ng');
      setError('Agora SDK ไม่ได้ติดตั้ง กรุณาติดตั้ง: npm install agora-rtc-sdk-ng');
      isJoiningRef.current = false;
      return;
    }
    
    if (!agoraConfig.appId) {
      console.error('❌ Agora App ID not configured. Please set REACT_APP_AGORA_APP_ID in .env and restart server');
      setError('Agora App ID ไม่ได้ตั้งค่า กรุณาตรวจสอบ .env และ restart server');
      // Fallback: show placeholder
      if (localVideoRef.current) {
        localVideoRef.current.style.backgroundColor = '#1f2937';
      }
      if (remoteVideoRef.current) {
        remoteVideoRef.current.style.backgroundColor = '#374151';
      }
      isJoiningRef.current = false;
      return;
    }

    try {
      // Initialize client if not exists
      if (!clientRef.current) {
        clientRef.current = await initializeAgoraClient();
      }

      if (!clientRef.current) {
        throw new Error('Failed to initialize Agora client');
      }

      // Generate channel name
      const channelName = chatId 
        ? `chat_${chatId}` 
        : `product_${productId}_store_${storeId}`;
      
      // Generate UID (use user ID hash or random number)
      const uid = currentUser?.uid 
        ? parseInt(currentUser.uid.slice(0, 8), 16) % 2147483647 
        : Math.floor(Math.random() * 100000);

      // For development: Join without token (if enabled in Agora Console)
      // For production: Get token from your server
      const token = null; // TODO: Get token from your token service

      // Check device availability before creating tracks
      console.log('🔍 Checking device availability...');
      let audioTrack = null;
      let videoTrack = null;
      
      try {
        // Check if getUserMedia is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Browser does not support camera/microphone access');
        }
        
        // Try to get available devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioDevices = devices.filter(d => d.kind === 'audioinput');
        const videoDevices = devices.filter(d => d.kind === 'videoinput');
        
        console.log('📱 Available devices:', {
          audio: audioDevices.length,
          video: videoDevices.length
        });
        
        // Create audio track (with error handling)
        try {
          audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
            encoderConfig: 'speech_standard'
          });
          localAudioTrackRef.current = audioTrack;
          console.log('✅ Audio track created successfully');
        } catch (audioError) {
          console.warn('⚠️ Failed to create audio track:', audioError);
          if (audioError.name === 'DEVICE_NOT_FOUND' || 
              audioError.message.includes('device not found') ||
              audioError.message.includes('NotFoundError')) {
            console.warn('⚠️ No microphone found. Continuing without audio...');
            // Continue without audio - user can still receive audio
            setIsMuted(true); // Set as muted since no mic
          } else if (audioError.name === 'NotAllowedError' || audioError.message.includes('permission')) {
            console.warn('⚠️ Microphone permission denied. Continuing without audio...');
            setIsMuted(true);
          } else {
            console.warn('⚠️ Audio error (non-critical):', audioError);
            setIsMuted(true);
            // Continue anyway - don't block the call
          }
        }
        
        // Create video track (with error handling)
        // Use lower resolution to prevent stuttering on mobile devices
        try {
          // Detect if mobile device
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
          
          // Use appropriate encoder config based on device
          const encoderConfig = isMobile ? '480p' : '720p_1';
          
          videoTrack = await AgoraRTC.createCameraVideoTrack({
            encoderConfig: encoderConfig,
            optimizationMode: 'motion', // Optimize for motion (better for video calls)
            frameRate: 15 // Limit frame rate to reduce stuttering
          });
          localVideoTrackRef.current = videoTrack;
          console.log('✅ Video track created successfully');
        } catch (videoError) {
          console.warn('⚠️ Failed to create video track:', videoError);
          if (videoError.name === 'DEVICE_NOT_FOUND' || 
              videoError.message.includes('device not found') ||
              videoError.message.includes('NotFoundError')) {
            console.warn('⚠️ No camera found. Continuing without video...');
            setIsVideoEnabled(false); // Set as video disabled
            // Show placeholder for local video
            if (localVideoRef.current) {
              localVideoRef.current.style.backgroundColor = '#1f2937';
              localVideoRef.current.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400"><div class="text-center"><svg class="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg><p class="text-sm">ไม่มีกล้อง</p></div></div>';
            }
          } else if (videoError.name === 'NotAllowedError' || videoError.message.includes('permission')) {
            console.warn('⚠️ Camera permission denied. Continuing without video...');
            setIsVideoEnabled(false);
            if (localVideoRef.current) {
              localVideoRef.current.style.backgroundColor = '#1f2937';
              localVideoRef.current.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400"><div class="text-center"><svg class="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg><p class="text-sm">ไม่อนุญาตกล้อง</p></div></div>';
            }
          } else {
            console.warn('⚠️ Video error (non-critical):', videoError);
            setIsVideoEnabled(false);
            // Continue anyway - don't block the call
          }
        }
        
        // Allow joining even without local tracks (user can still receive)
        // This is useful for devices without camera/microphone
        if (!audioTrack && !videoTrack) {
          console.warn('⚠️ No local tracks available. Joining in receive-only mode...');
          // Show info message
          setError(null); // Clear any previous errors
          // User can still receive audio/video from remote user
        }
        
      } catch (deviceError) {
        console.error('❌ Device error:', deviceError);
        
        // Provide helpful error messages
        if (deviceError.name === 'NotAllowedError' || deviceError.message.includes('permission')) {
          throw new Error('กรุณาอนุญาตการเข้าถึงกล้องและไมโครโฟนใน Browser Settings');
        } else if (deviceError.name === 'NotFoundError' || deviceError.message.includes('not found')) {
          throw new Error('ไม่พบกล้องหรือไมโครโฟน กรุณาตรวจสอบว่าได้เชื่อมต่ออุปกรณ์แล้ว');
        } else if (deviceError.name === 'NotReadableError' || deviceError.message.includes('not readable')) {
          throw new Error('กล้องหรือไมโครโฟนถูกใช้งานโดยแอปอื่น กรุณาปิดแอปอื่นก่อน');
        } else {
          throw deviceError;
        }
      }

      // Play local video (if video track exists)
      if (localVideoRef.current && videoTrack) {
        videoTrack.play(localVideoRef.current);
      }

      // Set connection state
      setConnectionState('connecting');

      // Check if still mounted before joining
      if (!isMountedRef.current) {
        console.warn('⚠️ Component unmounted before join, aborting...');
        throw new Error('Component unmounted');
      }
      
      // Join channel
      console.log('🔄 Joining channel:', channelName);
      await clientRef.current.join(agoraConfig.appId, channelName, token, uid);
      
      // Check again after join
      if (!isMountedRef.current) {
        console.warn('⚠️ Component unmounted after join, cleaning up...');
        await clientRef.current.leave();
        return;
      }
      
      // Publish local tracks (only publish tracks that exist)
      const tracksToPublish = [];
      if (audioTrack) tracksToPublish.push(audioTrack);
      if (videoTrack) tracksToPublish.push(videoTrack);
      
      if (tracksToPublish.length > 0) {
        await clientRef.current.publish(tracksToPublish);
        console.log(`✅ Published ${tracksToPublish.length} track(s):`, {
          audio: !!audioTrack,
          video: !!videoTrack
        });
      } else {
        // No local tracks to publish - join in receive-only mode
        console.log('ℹ️ Joining channel in receive-only mode (no local tracks)');
        console.log('ℹ️ You can still receive audio/video from the other user');
        // This is fine - user can still receive from remote
      }

      setConnectionState('connected');
      console.log('✅ Successfully joined Agora channel:', channelName);
      isJoiningRef.current = false;
    } catch (error) {
      isJoiningRef.current = false;
      
      // Ignore OPERATION_ABORTED if component is unmounting
      if (!isMountedRef.current && (error.name === 'OPERATION_ABORTED' || error.message?.includes('cancel token canceled'))) {
        console.log('ℹ️ Operation aborted due to component unmount (expected)');
        return;
      }
      
      console.error('❌ Error joining Agora channel:', error);
      setConnectionState('disconnected');
      
      // Provide user-friendly error messages
      let errorMessage = 'เกิดข้อผิดพลาดในการเชื่อมต่อ Video Call';
      
      if (error.message) {
        if (error.message.includes('permission') || error.message.includes('อนุญาต')) {
          errorMessage = error.message;
        } else if (error.message.includes('not found') || error.message.includes('ไม่พบ')) {
          errorMessage = error.message;
        } else if (error.message.includes('not readable') || error.message.includes('ถูกใช้งาน')) {
          errorMessage = error.message;
        } else if (error.name === 'DEVICE_NOT_FOUND' || error.message.includes('DEVICE_NOT_FOUND')) {
          errorMessage = 'ไม่พบกล้องหรือไมโครโฟน กรุณาตรวจสอบว่าได้อนุญาตการเข้าถึงแล้ว และอุปกรณ์เชื่อมต่ออยู่';
        } else if (error.name === 'OPERATION_ABORTED' || error.message.includes('cancel token canceled')) {
          errorMessage = 'การเชื่อมต่อถูกยกเลิก กรุณาลองใหม่อีกครั้ง';
        } else {
          errorMessage = error.message;
        }
      } else {
        errorMessage += ': ' + (error.toString() || 'Unknown error');
      }
      
      setError(errorMessage);
      
      // Clean up on error
      try {
        if (localAudioTrackRef.current) {
          localAudioTrackRef.current.stop();
          localAudioTrackRef.current.close();
          localAudioTrackRef.current = null;
        }
        if (localVideoTrackRef.current) {
          localVideoTrackRef.current.stop();
          localVideoTrackRef.current.close();
          localVideoTrackRef.current = null;
        }
        if (clientRef.current) {
          await clientRef.current.leave();
          clientRef.current = null;
        }
      } catch (cleanupError) {
        console.error('Error during cleanup:', cleanupError);
      }
      
      // Fallback: show placeholder
      if (localVideoRef.current) {
        localVideoRef.current.style.backgroundColor = '#1f2937';
      }
      if (remoteVideoRef.current) {
        remoteVideoRef.current.style.backgroundColor = '#374151';
      }
    }
  };

  const leaveAgoraChannel = async () => {
    try {
      // Stop and close local tracks
      if (localAudioTrackRef.current) {
        localAudioTrackRef.current.stop();
        localAudioTrackRef.current.close();
        localAudioTrackRef.current = null;
      }
      
      if (localVideoTrackRef.current) {
        localVideoTrackRef.current.stop();
        localVideoTrackRef.current.close();
        localVideoTrackRef.current = null;
      }

      // Leave channel
      if (clientRef.current) {
        await clientRef.current.leave();
        clientRef.current = null;
      }

      // Clear remote users
      remoteUsersRef.current = [];
      
      // Clear video elements
      if (localVideoRef.current) {
        localVideoRef.current.innerHTML = '';
      }
      if (remoteVideoRef.current) {
        remoteVideoRef.current.innerHTML = '';
      }
      
      setConnectionState('disconnected');
    } catch (error) {
      console.error('Error leaving Agora channel:', error);
      setConnectionState('disconnected');
    }
  };

  const toggleMute = async () => {
    try {
      if (localAudioTrackRef.current) {
        if (isMuted) {
          await localAudioTrackRef.current.setEnabled(true);
          setIsMuted(false);
        } else {
          await localAudioTrackRef.current.setEnabled(false);
          setIsMuted(true);
        }
      }
    } catch (error) {
      console.error('Error toggling mute:', error);
    }
  };

  const toggleVideo = async () => {
    try {
      if (localVideoTrackRef.current) {
        if (isVideoEnabled) {
          await localVideoTrackRef.current.setEnabled(false);
          setIsVideoEnabled(false);
          // Show placeholder
          if (localVideoRef.current) {
            localVideoRef.current.style.backgroundColor = '#1f2937';
            localVideoRef.current.innerHTML = '<div class="flex items-center justify-center h-full text-gray-400"><svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg></div>';
          }
        } else {
          await localVideoTrackRef.current.setEnabled(true);
          setIsVideoEnabled(true);
          // Play video again
          if (localVideoRef.current && localVideoTrackRef.current) {
            localVideoRef.current.innerHTML = '';
            localVideoTrackRef.current.play(localVideoRef.current);
          }
        }
      }
    } catch (error) {
      console.error('Error toggling video:', error);
    }
  };

  const initializeVideoCall = async () => {
    // Join Agora channel
    await joinAgoraChannel();
  };

  const endCall = async () => {
    try {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
      
      // Calculate duration
      const duration = callStartTimeRef.current 
        ? Math.floor((new Date() - callStartTimeRef.current) / 1000)
        : 0;
      
      // Leave Agora channel
      await leaveAgoraChannel();
      
      // Update call record and usage
      if (callIdRef.current) {
        await endVideoCallDB(callIdRef.current, duration);
        
        // Update usage with final duration (ไม่เพิ่ม totalCalls เพราะเพิ่มแล้วตอน approve)
        try {
          const { getVideoCallUsage } = await import('../../utils/videoCallDatabase');
          const usage = await getVideoCallUsage(currentUser?.uid, true);
          const usageRef = doc(db, 'videoCallUsage', usage.id);
          await updateDoc(usageRef, {
            totalDuration: (usage.totalDuration || 0) + duration,
            completedCalls: (usage.completedCalls || 0) + 1,
            updatedAt: serverTimestamp()
          });
          console.log('✅ Updated video call usage with duration:', duration);
        } catch (usageError) {
          console.error('Error updating video call usage:', usageError);
        }
      }
      
      setCallStatus('ended');
      
      // Navigate back after 2 seconds
      setTimeout(() => {
        if (chatId) {
          navigate(`/marketplace/chat/${chatId}`);
        } else if (productId) {
          navigate(`/marketplace/products/${productId}`);
        } else {
          navigate('/marketplace');
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error ending call:', error);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            กลับ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 p-4 z-10">
        <div className="flex items-center justify-between">
          <button
            onClick={endCall}
            className="p-2 bg-gray-800 rounded-full hover:bg-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {callStatus === 'active' && (
            <div className="text-center">
              <p className="text-sm text-gray-400">ระยะเวลา</p>
              <p className="text-lg font-semibold">{formatDuration(callDuration)}</p>
            </div>
          )}
          
          <div className="w-10"></div>
        </div>
      </div>

      {/* Video Area */}
      <div className="h-screen flex items-center justify-center relative">
        {callStatus === 'connecting' && (
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-xl">กำลังเชื่อมต่อ...</p>
          </div>
        )}
        
        {callStatus === 'ringing' && (
          <div className="text-center">
            <div className="w-32 h-32 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <p className="text-xl mb-2">กำลังเรียก...</p>
            <p className="text-gray-400">
              {otherUser?.displayName || otherUser?.name || store?.name || 'ผู้ใช้'}
            </p>
          </div>
        )}
        
        {callStatus === 'active' && (
          <div className="w-full h-full relative">
            {/* Remote Video */}
            <div 
              className={`absolute transition-all duration-300 ${
                videoLayout === 'remote-full' 
                  ? 'inset-0 z-10' 
                  : videoLayout === 'local-full'
                  ? 'hidden'
                  : 'inset-0 lg:inset-auto lg:left-0 lg:top-0 lg:w-1/2 lg:h-full'
              } bg-gray-800 rounded-lg overflow-hidden`}
            >
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 px-3 py-1 rounded">
                <p className="text-sm">
                  {otherUser?.displayName || otherUser?.name || store?.name || 'ผู้ใช้'}
                </p>
              </div>
              {videoLayout !== 'remote-full' && (
                <button
                  onClick={() => setVideoLayout('remote-full')}
                  className="absolute top-4 right-4 bg-black bg-opacity-50 hover:bg-opacity-70 p-2 rounded-full transition-colors"
                  title="ขยายวิดีโอ"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </button>
              )}
              {videoLayout === 'remote-full' && (
                <button
                  onClick={() => setVideoLayout('split')}
                  className="absolute top-4 right-4 bg-black bg-opacity-50 hover:bg-opacity-70 p-2 rounded-full transition-colors"
                  title="ย่อวิดีโอ"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                  </svg>
                </button>
              )}
            </div>
            
            {/* Local Video */}
            <div 
              className={`absolute transition-all duration-300 ${
                videoLayout === 'local-full'
                  ? 'inset-0 z-10'
                  : videoLayout === 'remote-full'
                  ? 'hidden'
                  : 'bottom-4 right-4 lg:bottom-4 lg:right-4 w-32 h-32 lg:w-48 lg:h-48 rounded-lg overflow-hidden border-2 border-white shadow-lg z-20'
              } bg-gray-800`}
            >
              {localVideoTrackRef.current ? (
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                  <div className="text-center text-gray-400">
                    <svg className="w-8 h-8 lg:w-12 lg:h-12 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    <p className="text-xs">ไม่มีกล้อง</p>
                  </div>
                </div>
              )}
              <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 px-2 py-0.5 rounded text-xs">
                <p>คุณ</p>
                {isMuted && !localAudioTrackRef.current && (
                  <p className="text-red-400">🔇</p>
                )}
              </div>
              {videoLayout !== 'local-full' && (
                <button
                  onClick={() => setVideoLayout('local-full')}
                  className="absolute top-1 right-1 bg-black bg-opacity-50 hover:bg-opacity-70 p-1 rounded transition-colors"
                  title="ขยายวิดีโอของคุณ"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </button>
              )}
              {videoLayout === 'local-full' && (
                <button
                  onClick={() => setVideoLayout('split')}
                  className="absolute top-4 right-4 bg-black bg-opacity-50 hover:bg-opacity-70 p-2 rounded-full transition-colors"
                  title="ย่อวิดีโอ"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
        
        {callStatus === 'ended' && (
          <div className="text-center">
            <div className="w-32 h-32 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-xl mb-2">การโทรจบแล้ว</p>
            <p className="text-gray-400">ระยะเวลา: {formatDuration(callDuration)}</p>
          </div>
        )}
      </div>

      {/* Controls */}
      {callStatus === 'active' && (
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gray-900 bg-opacity-75">
          <div className="flex items-center justify-center gap-4">
            {/* Mute/Unmute Button */}
            <button
              onClick={toggleMute}
              className={`p-4 rounded-full transition-colors ${
                isMuted 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
              title={isMuted ? 'เปิดเสียง' : 'ปิดเสียง'}
            >
              {isMuted ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M17 10l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              )}
            </button>

            {/* Camera On/Off Button */}
            <button
              onClick={toggleVideo}
              className={`p-4 rounded-full transition-colors ${
                !isVideoEnabled 
                  ? 'bg-red-500 hover:bg-red-600' 
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
              title={isVideoEnabled ? 'ปิดกล้อง' : 'เปิดกล้อง'}
            >
              {isVideoEnabled ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              )}
            </button>

            {/* End Call Button */}
            <button
              onClick={endCall}
              className="p-4 bg-red-500 rounded-full hover:bg-red-600 transition-colors"
              title="วางสาย"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Connection Status */}
          {connectionState === 'connecting' && (
            <div className="mt-4 text-center">
              <p className="text-sm text-yellow-400">กำลังเชื่อมต่อ...</p>
            </div>
          )}
          {connectionState === 'connected' && (
            <div className="mt-4 text-center">
              <p className="text-sm text-green-400">เชื่อมต่อสำเร็จ</p>
            </div>
          )}
          {connectionState === 'disconnected' && callStatus === 'active' && (
            <div className="mt-4 text-center">
              <p className="text-sm text-red-400">การเชื่อมต่อขาด</p>
            </div>
          )}
        </div>
      )}

      {/* Product Info (if available) */}
      {product && callStatus === 'active' && (
        <div className="absolute top-20 left-4 bg-black bg-opacity-50 p-4 rounded-lg max-w-xs">
          <p className="text-xs text-gray-400 mb-1">กำลังคุยเกี่ยวกับสินค้า:</p>
          <p className="text-sm font-semibold">{product.name}</p>
          {product.price && (
            <p className="text-xs text-gray-400">฿{product.price.toLocaleString()}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoCall;

